/**
 * The ONE reader of an api error's RES-ENV-001 machine code, and the `cause`-chain walk it reads through.
 * Beside it, the ONE reader of an api error's details LIST (`readErrorDetailList`, `findValidationDetail`; PPUR item 5d).
 *
 * Zero imports, so a leaf package (feedback-ui, re-auth) reads the one rule from the `./error-code` subpath without
 * taking the error-handling barrel (the feature-flags `/flag-value` precedent). The barrel re-exports both from here.
 *
 * @module @puffin-core/error-handling/errorCode
 */

/**
 * Walk an error and its `cause` chain, nearest first.
 *
 * ## Why the chain is not optional (AAS-I18N-002 §11.6, measured 2026-08-11)
 *
 * A service layer that adds context by re-throwing
 * `new Error(\`Failed to X: \${error.message}\`, { cause: error })` **destroys the RES-ENV-001
 * envelope** — `code`, `status` and `details` are all gone, and the only thing that survives to the
 * UI is an English sentence with an English prefix. `pos-web`'s `timecardService` does this at **19
 * sites**, and it is the direct reason the clock buttons matched prose: by the time the error
 * reached the component, prose was genuinely all that was left.
 *
 * Rewriting 19 throw sites would fix 19 throw sites. Reading the `cause` chain here fixes every
 * wrapper in the ecosystem, including the ones nobody has found yet, and it costs the wrapper
 * nothing — `{ cause }` is the standard JS mechanism for exactly this and the sites already pass it.
 *
 * Depth is bounded and visited nodes are tracked: a `cause` cycle would otherwise hang the render
 * that resolved the message.
 *
 * @param {*} error
 * @param {number} [maxDepth]
 * @yields {*} each error in the chain, starting with `error` itself
 */
export function* eachErrorInChain(error, maxDepth = 8) {
  const seen = new Set();
  let cur = error;
  for (let i = 0; i < maxDepth && cur && typeof cur === 'object'; i += 1) {
    if (seen.has(cur)) return;
    seen.add(cur);
    yield cur;
    cur = cur.cause;
  }
}

/**
 * Read the RES-ENV-001 machine `code` off an api error, in either error shape.
 *
 * ## Why a client must never branch on the server's PROSE (AAS-I18N-002 §11.6)
 *
 * A client that decides which translated sentence to show by substring-matching the server's
 * `message` has two failure modes, and both were live in this repo when this was written:
 *
 *   1. **The matcher silently dies.** `pos-web` tested `err.message?.includes('No active time
 *      punch')` while pos-api sent `'No active punch found to clock out'`. The branch had a **0 %
 *      hit rate**; every user fell through to the generic handler. Nothing failed — not a test, not
 *      a lint rule, not a census — because a dead branch is indistinguishable from an unused one.
 *   2. **English leaks into the Spanish UI.** The fall-through then interpolated the server's
 *      English sentence into a Spanish template, so the reader saw half a translation.
 *
 * A `code` cannot rot silently in either direction: it is a bounded vocabulary both sides declare,
 * and it survives copy edits, because copy is exactly what it is not.
 *
 * @param {*} error - a processed api error, a raw axios error, or a wrapper carrying either as
 *   its `cause` (see {@link eachErrorInChain})
 * @returns {?string} the machine code, or `null` when absent/not a string
 */
export function readErrorCode(error) {
  for (const e of eachErrorInChain(error)) {
    // The server's code is the hoisted `errorCode` (Q-RC-RESPONSE-ARM, 2026-08-28), ahead of the error's own `code`.
    const fromProcessed = e?.errorCode ?? e?.code;
    const fromRaw = e?.response?.data?.error?.code;
    const code = typeof fromProcessed === 'string' ? fromProcessed : fromRaw;
    if (typeof code === 'string' && code.length > 0) return code;
  }
  return null;
}

/**
 * The refusal codes whose `details` are a LIST of field problems: the ONE request validator's `VALIDATION_ERROR`
 * (`{ field, rule, params, message }`, item 5e) and its `FIELD_NOT_ACCEPTED` (`{ field, reason }`).
 */
const VALIDATION_DETAIL_CODES = Object.freeze(['VALIDATION_ERROR', 'FIELD_NOT_ACCEPTED']);

/**
 * Read an api error's `details` when they are a LIST, in either error shape, walking the `cause` chain.
 *
 * {@link readErrorDetails} reads the details OBJECT a coded refusal carries; a 422's details are an ARRAY — one entry
 * per refused field (`{ field, rule, params, message }`), or one per refused member of a typed refusal (a station, a
 * team member). Every screen used to read that array by hand with `Array.isArray(err.errorDetails) ? … : []`, which
 * knows only the processed shape and stops at the first wrapper (PPUR item 5d). This is the ONE reader, and
 * `web/no-hand-rolled-error-detail-read` keeps it so.
 *
 * @param {*} error - a processed api error, or a raw axios error
 * @returns {Array<Object>} the nearest `details` that is an array, or `[]`
 */
export function readErrorDetailList(error) {
  for (const e of eachErrorInChain(error)) {
    const carriers = [e?.errorDetails, e?.data?.details, e?.response?.data?.error?.details];
    const list = carriers.find((candidate) => Array.isArray(candidate));
    if (list) return list;
  }
  return [];
}

/**
 * Find the one entry of a field refusal (`VALIDATION_ERROR` / `FIELD_NOT_ACCEPTED`) for `field`, and, when `rule` is
 * given, for that rule: the value-free `{ field, rule, params, message }` the validator sends (item 5e). A client tells
 * two refusals of one field apart by `rule` (and words them from its own catalog), never by the server's English
 * `message`.
 *
 * @param {*} error - a processed api error, or a raw axios error
 * @param {Object} spec
 * @param {string} spec.field - the refused field, as the validator names it
 * @param {string} [spec.rule] - the refusing rule (e.g. `'date.max'`); any rule when omitted
 * @returns {?Object} the matching detail, or `null` when the failure is not a field refusal or names no such field
 * @throws {TypeError} when `field` is not a non-empty string
 */
export function findValidationDetail(error, { field, rule } = {}) {
  if (typeof field !== 'string' || field.length === 0) {
    throw new TypeError('findValidationDetail: `field` must be a non-empty string');
  }
  if (!VALIDATION_DETAIL_CODES.includes(readErrorCode(error))) return null;
  const detail = readErrorDetailList(error).find(
    (candidate) => candidate?.field === field && (rule === undefined || candidate.rule === rule),
  );
  return detail ?? null;
}
